/* global BigInt */
import React, { useState, useRef } from "react";
import Registers from "./Registers";
import Memory from "./Memory";
import Log from "./Log";
import CodeEditor from "./CodeEditor";

export default function App() {
  // ---------------- DEFAULT STATE OBJECT ----------------
  const createInitialState = () => ({
    registers: (() => {
      const r = Array(32).fill("0000000000000000");
      r[0] = "0000000000000000"; // x0
      r[2] = "0000000000000FFF"; // x2, stack pointer
      return r;
    })(),
    pc: 0n,
    memory: Array(4096).fill("00"),
    code: "",
    log: [],
    memPage: 0,
    currentLine: 0,
  });

  const initial = createInitialState();

  // ---------------- STATES ----------------
  const [registers, setRegisters] = useState(initial.registers);
  const registersRef = useRef(registers);
  const runRef = useRef(false);
  const pauseRef = useRef(false);
  const isRunningRef = useRef(false);
  const isPausedRef = useRef(false);



  React.useEffect(() => {
    registersRef.current = registers;
  }, [registers]);

  const [pc, setPc] = useState(initial.pc);
  const [memory, setMemory] = useState(initial.memory);
  const [code, setCode] = useState(initial.code);
  const [log, setLog] = useState(initial.log);
  const [memPage, setMemPage] = useState(initial.memPage);
  const [currentLine, setCurrentLine] = useState(initial.currentLine);
  const [regView, setRegView] = useState("hex");
  const [memView, setMemView] = useState("hex");
  const [activeRunButton, setActiveRunButton] = useState(null);

  // lineIndex -> instruction address (Number)
  const [assembledMap, setAssembledMap] = useState({});

  // lineIndex -> branch offset in bytes (BigInt)
  const [branchOffsetMap, setBranchOffsetMap] = useState({});

  // lineIndex -> jal target address (Number) (resolved at assembly)
  const [jalTargetAddrMap, setJalTargetAddrMap] = useState({});

  // addr (Number) -> lineIndex (for jumping by address)
  const [addrToLineMap, setAddrToLineMap] = useState({});

  const [highlightedAddrs, setHighlightedAddrs] = useState([]);
  const [assemblyOk, setAssemblyOk] = useState(false);
  const [executionHalted, setExecutionHalted] = useState(false);

  const SEGMENTS = {
    text: { start: 0, size: 2048, color: "#00bcd4" },
    data: { start: 2048, size: 1024, color: "#4caf50" },
    heap: { start: 3072, size: 256, color: "#ff9800" },
    stack: { start: 3328, size: 4096 - 3328, color: "#e91e63" },
  };

  const addLog = (msg) => setLog((prev) => [...prev, msg]);

  const writeReg = (rd, hexValue) => {
  if (rd === 0) return;

    // update ref immediately (CRITICAL for run loop)
    const next = [...registersRef.current];
    next[rd] = hexValue;
    registersRef.current = next;

    // update React state for UI
    setRegisters(next);
  };



  



  // ---------------- PARSING / FORMATTING ----------------
  const formatRegisterForView = (hexStr, view) => {
    if (!hexStr) return "";
    const big = BigInt("0x" + hexStr);
    if (view === "hex") return "0x" + hexStr.toUpperCase();
    if (view === "bin") return "0b" + big.toString(2).padStart(64, "0");
    return BigInt.asIntN(64, big).toString();
  };

  // ---------------- IMMEDIATE PARSING ----------------
  const parseSigned12 = (s) => {
    if (s == null) return 0;
    const trimmed = String(s).trim();
    if (trimmed.startsWith("0x") || trimmed.startsWith("0b")) return null;
    const n = parseInt(trimmed, 10);
    if (Number.isNaN(n)) return null;
    if (n < -2048 || n > 2047) return null;
    return n;
  };

  const parseDecimal = (s) => {
    if (s == null) return null;
    const trimmed = String(s).trim();
    if (trimmed.startsWith("0x") || trimmed.startsWith("0b")) return null;
    const n = parseInt(trimmed, 10);
    return Number.isNaN(n) ? null : n;
  };

  // ---------------- INSTRUCTION CONFIG ----------------
  const RTypeInstructions = ["add", "sub", "xor", "or", "and", "sll", "srl", "mul", "div"];
  const ITypeInstructions = [
    "addi",
    "xori",
    "ori",
    "andi",
    "lb",
    "lbu",
    "lh",
    "lhu",
    "lw",
    "lwu",
    "ld",
    "slli",
    "srli",
    "jalr", // NEW
  ];
  const STypeInstructions = ["sb", "sh", "sw", "sd"];
  const UTypeInstructions = ["lui"];
  const SBTypeInstructions = ["beq", "bne", "blt", "bge"]; // (spec name: SB-type)
  const UJTypeInstructions = ["jal"]; // NEW (UJ-type)

  const parseRegister = (str) =>
    typeof str === "string" && str.startsWith("x") ? parseInt(str.slice(1), 10) : NaN;

  // ---- label helpers ----
  const stripComments = (raw) => raw.split("#")[0];
  const extractLabelsAndRemainder = (rawLine) => {
    let line = stripComments(rawLine).trim();
    const labels = [];
    while (true) {
      const m = line.match(/^([A-Za-z_]\w*):\s*(.*)$/);
      if (!m) break;
      labels.push(m[1]);
      line = (m[2] || "").trim();
    }
    return { labels, remainder: line };
  };

  // ---------------- ENCODERS ----------------
  const encodeRType = (instr, rd, rs1, rs2) => {
    const funct7Map = {
      add: 0x00,
      sub: 0x20,
      xor: 0x00,
      or: 0x00,
      and: 0x00,
      sll: 0x00,
      srl: 0x00,
      mul: 0x01,
      div: 0x01,
    };
    const funct3Map = {
      add: 0x0,
      sub: 0x0,
      xor: 0x4,
      or: 0x6,
      and: 0x7,
      sll: 0x1,
      srl: 0x5,
      mul: 0x0,
      div: 0x4,
    };
    const opcode = 0x33;
    const funct7 = funct7Map[instr] || 0;
    const funct3 = funct3Map[instr] || 0;
    return (
      ((funct7 & 0x7f) << 25) |
      ((rs2 & 0x1f) << 20) |
      ((rs1 & 0x1f) << 15) |
      ((funct3 & 0x7) << 12) |
      ((rd & 0x1f) << 7) |
      (opcode & 0x7f)
    );
  };

  const encodeIType = (instr, rd, rs1, imm) => {
    const funct3Map = {
      addi: 0x0,
      xori: 0x4,
      ori: 0x6,
      andi: 0x7,

      lb: 0x0,
      lbu: 0x4,
      lh: 0x1,
      lhu: 0x5,
      lw: 0x2,
      lwu: 0x6,
      ld: 0x3,

      slli: 0x1,
      srli: 0x5,

      jalr: 0x0, // NEW (funct3=000)
    };
    const opcodeMap = {
      addi: 0x13,
      xori: 0x13,
      ori: 0x13,
      andi: 0x13,

      lb: 0x03,
      lbu: 0x03,
      lh: 0x03,
      lhu: 0x03,
      lw: 0x03,
      lwu: 0x03,
      ld: 0x03,

      slli: 0x13,
      srli: 0x13,

      jalr: 0x67, // NEW
    };

    const funct7Map = { slli: 0x00, srli: 0x00 };

    // RV64I shifts (use imm[5:0])
    if (instr === "slli" || instr === "srli") {
      return (
        ((funct7Map[instr] & 0x3f) << 26) |
        ((imm & 0x3f) << 20) |
        ((rs1 & 0x1f) << 15) |
        ((funct3Map[instr] & 0x7) << 12) |
        ((rd & 0x1f) << 7) |
        (opcodeMap[instr] & 0x7f)
      );
    }

    return (
      ((imm & 0xfff) << 20) |
      ((rs1 & 0x1f) << 15) |
      ((funct3Map[instr] & 0x7) << 12) |
      ((rd & 0x1f) << 7) |
      (opcodeMap[instr] & 0x7f)
    );
  };

  const encodeSType = (instr, rs1, rs2, imm) => {
    const funct3Map = { sb: 0x0, sh: 0x1, sw: 0x2, sd: 0x3 };
    const opcode = 0x23;
    const imm11_5 = (imm >> 5) & 0x7f;
    const imm4_0 = imm & 0x1f;
    return (
      ((imm11_5 & 0x7f) << 25) |
      ((rs2 & 0x1f) << 20) |
      ((rs1 & 0x1f) << 15) |
      ((funct3Map[instr] & 0x7) << 12) |
      ((imm4_0 & 0x1f) << 7) |
      (opcode & 0x7f)
    );
  };

  const encodeUType = (instr, rd, imm) => {
    const opcodeMap = { lui: 0x37 };
    return ((imm & 0xfffff) << 12) | ((rd & 0x1f) << 7) | (opcodeMap[instr] & 0x7f);
  };

  // SB-type (branches): offset in BYTES, multiple of 2, range [-4096, +4094]
  const encodeSBType = (instr, rs1, rs2, offsetBytes) => {
    const funct3Map = { beq: 0x0, bne: 0x1, blt: 0x4, bge: 0x5 };
    const opcode = 0x63;

    const off = offsetBytes | 0; // small range
    const imm12 = (off >> 12) & 0x1;
    const imm11 = (off >> 11) & 0x1;
    const imm10_5 = (off >> 5) & 0x3f;
    const imm4_1 = (off >> 1) & 0x0f;

    const funct3 = funct3Map[instr];
    return (
      ((imm12 & 0x1) << 31) |
      ((imm10_5 & 0x3f) << 25) |
      ((rs2 & 0x1f) << 20) |
      ((rs1 & 0x1f) << 15) |
      ((funct3 & 0x7) << 12) |
      ((imm4_1 & 0x0f) << 8) |
      ((imm11 & 0x1) << 7) |
      (opcode & 0x7f)
    );
  };

  // UJ-type (jal): offset in BYTES, multiple of 2, range [-1048576, +1048574]
  const encodeUJType = (rd, offsetBytes) => {
    const opcode = 0x6f;
    const off = offsetBytes | 0; // fits 32-bit in our memory size anyway

    // Bits come from byte offset (bit0 always 0)
    const imm20 = (off >> 20) & 0x1; // sign bit
    const imm10_1 = (off >> 1) & 0x3ff;
    const imm11 = (off >> 11) & 0x1;
    const imm19_12 = (off >> 12) & 0xff;

    return (
      ((imm20 & 0x1) << 31) |
      ((imm10_1 & 0x3ff) << 21) |
      ((imm11 & 0x1) << 20) |
      ((imm19_12 & 0xff) << 12) |
      ((rd & 0x1f) << 7) |
      (opcode & 0x7f)
    );
  };

  // const lines = code.split("\n");
  // const lines = code.replace(/\n+$/, "").split("\n");
  const lines = code
  .replace(/\r\n/g, "\n")
  .replace(/\r/g, "\n")
  .split("\n");


  const formatBinary32 = (num) => (num >>> 0).toString(2).padStart(32, "0");
  const toHex32 = (num) => "0x" + (num >>> 0).toString(16).padStart(8, "0").toUpperCase();

  // ---------------- FIX: sync memory highlight to PC/currentLine after render ----------------
  // ---------------- PC-driven highlight (PC is source of truth) ----------------
  React.useEffect(() => {
    const addr = Number(pc);

    // highlight current instruction (4 bytes)
    setHighlightedAddrs([addr, addr + 1, addr + 2, addr + 3]);

    // derive currentLine from PC
    const line = addrToLineMap[addr];
    if (line !== undefined) {
      setCurrentLine(line);
    }
  }, [pc, addrToLineMap]);


  // ---------------- ASSEMBLE (TWO-PASS WITH LABELS) ----------------
  const assembleCode = () => {
    runRef.current = false;
    pauseRef.current = false;

    setAssemblyOk(false);
    setExecutionHalted(false);

    const newMemory = [...memory];
    const newMap = {};
    const newBranchOffsets = {};
    const newAddrToLine = {};
    const newJalTargets = {};

    addLog("----- Assembling program -----");

    // PASS 1: labels
    let tempPC = 0n;
    const labelMap = {};

    for (let i = 0; i < lines.length; i++) {
      const { labels, remainder } = extractLabelsAndRemainder(lines[i]);

      for (const lbl of labels) {
        if (labelMap[lbl] !== undefined) {
          addLog(`❌ Assembly error line ${i + 1}: Duplicate label '${lbl}'`);
          addLog("Assembly aborted.");
          return;
        }
        labelMap[lbl] = tempPC;
      }

      if (remainder) tempPC += 4n;
    }

    // PASS 2: encode
    let currentPC = 0n;

    setPc(0n);
    setCurrentLine(0);

    for (let index = 0; index < lines.length; index++) {
      const { remainder } = extractLabelsAndRemainder(lines[index]);
      const line = remainder;
      if (!line) continue;

      const tokens = line.split(/[\s,]+/).filter(Boolean);
      const instr = (tokens[0] || "").toLowerCase();

      const addrNum = Number(currentPC);

      // -------- R-TYPE --------
      if (RTypeInstructions.includes(instr)) {
        if (tokens.length < 4) {
          addLog(`❌ Assembly error line ${index + 1}: Expected 3 operands for ${instr}`);
          addLog("Assembly aborted.");
          return;
        }
        const rd = parseRegister(tokens[1]);
        const rs1 = parseRegister(tokens[2]);
        const rs2 = parseRegister(tokens[3]);
        if ([rd, rs1, rs2].some((x) => Number.isNaN(x))) {
          addLog(`❌ Invalid register in '${line}'`);
          addLog("Assembly aborted.");
          return;
        }

        const binary = encodeRType(instr, rd, rs1, rs2);
        newMap[index] = addrNum;
        newAddrToLine[addrNum] = index;

        addLog(`Line ${index + 1}: ${line}`);
        addLog(`Binary: ${formatBinary32(binary)}`);
        addLog(`Hex: ${toHex32(binary)}`);
        addLog(`Stored at memory address starting: 0x${addrNum.toString(16).padStart(3, "0")}`);

        const bytes = [binary & 0xff, (binary >> 8) & 0xff, (binary >> 16) & 0xff, (binary >> 24) & 0xff];
        bytes.forEach((b, i) => {
          const a = addrNum + i;
          if (a >= 0 && a < newMemory.length) newMemory[a] = b.toString(16).padStart(2, "0").toUpperCase();
        });
        addLog(`Memory bytes stored (little-endian): ${bytes.map((b) => b.toString(16).padStart(2, "0").toUpperCase()).join(" ")}`);

        currentPC += 4n;
      }

      // -------- I-TYPE INCLUDING LOADS + JALR --------
      else if (ITypeInstructions.includes(instr)) {
        // JALR format: jalr rd, imm(rs1)
        if (instr === "jalr") {
          if (tokens.length < 3) {
            addLog(`❌ Assembly error line ${index + 1}: Expected 2 operands for jalr`);
            addLog("Assembly aborted.");
            return;
          }
          const rd = parseRegister(tokens[1]);
          const match = tokens[2].match(/^(-?\d+)\((x\d+)\)$/);
          if (!match) {
            addLog(`❌ Invalid jalr operand '${tokens[2]}' on line ${index + 1} (use imm(xN))`);
            addLog("Assembly aborted.");
            return;
          }
          const immParsed = parseSigned12(match[1]);
          const rs1 = parseRegister(match[2]);
          if ([rd, rs1].some((x) => Number.isNaN(x)) || immParsed === null) {
            addLog(`❌ Invalid register or immediate in '${line}'`);
            addLog("Assembly aborted.");
            return;
          }

          const binary = encodeIType("jalr", rd, rs1, immParsed);
          newMap[index] = addrNum;
          newAddrToLine[addrNum] = index;

          addLog(`Line ${index + 1}: ${line}`);
          addLog(`Binary: ${formatBinary32(binary)}`);
          addLog(`Hex: ${toHex32(binary)}`);
          addLog(`Stored at memory address starting: 0x${addrNum.toString(16).padStart(3, "0")}`);

          const bytes = [binary & 0xff, (binary >> 8) & 0xff, (binary >> 16) & 0xff, (binary >> 24) & 0xff];
          bytes.forEach((b, i) => {
            const a = addrNum + i;
            if (a >= 0 && a < newMemory.length) newMemory[a] = b.toString(16).padStart(2, "0").toUpperCase();
          });
          addLog(`Memory bytes stored (little-endian): ${bytes.map((b) => b.toString(16).padStart(2, "0").toUpperCase()).join(" ")}`);

          currentPC += 4n;
          addLog("");
          continue;
        }

        // other I-type: loads or imm arith/shift
        const rd = parseRegister(tokens[1]);
        let rs1, immParsed;

        if (["lb", "lbu", "lh", "lhu", "lw", "lwu", "ld"].includes(instr)) {
          if (tokens.length < 3) {
            addLog(`❌ Assembly error line ${index + 1}: Expected 2 operands for ${instr}`);
            addLog("Assembly aborted.");
            return;
          }
          const match = tokens[2].match(/^(-?\d+)\((x\d+)\)$/);
          if (!match) {
            addLog(`❌ Invalid memory operand '${tokens[2]}' on line ${index + 1}`);
            addLog("Assembly aborted.");
            return;
          }
          immParsed = parseSigned12(match[1]);
          rs1 = parseRegister(match[2]);
          if ([rd, rs1].some((x) => Number.isNaN(x)) || immParsed === null) {
            addLog(`❌ Invalid register or immediate in '${lines[index]}'`);
            addLog("Assembly aborted.");
            return;
          }
        } else {
          if (tokens.length < 4) {
            addLog(`❌ Assembly error line ${index + 1}: Expected 3 operands for ${instr}`);
            addLog("Assembly aborted.");
            return;
          }
          rs1 = parseRegister(tokens[2]);

          if (["slli", "srli"].includes(instr)) {
            immParsed = parseDecimal(tokens[3]);
            if ([rd, rs1].some(Number.isNaN) || immParsed === null || immParsed < 0 || immParsed > 63) {
              addLog(`❌ Invalid register or shift immediate for '${instr}'`);
              addLog("Assembly aborted.");
              return;
            }
          } else {
            immParsed = parseSigned12(tokens[3]);
            if ([rd, rs1].some(Number.isNaN) || immParsed === null) {
              addLog(`❌ Invalid register or immediate in '${lines[index]}'`);
              addLog("Assembly aborted.");
              return;
            }
          }
        }

        const binary = encodeIType(instr, rd, rs1, immParsed);
        newMap[index] = addrNum;
        newAddrToLine[addrNum] = index;

        addLog(`Line ${index + 1}: ${lines[index]}`);
        addLog(`Binary: ${formatBinary32(binary)}`);
        addLog(`Hex: ${toHex32(binary)}`);
        addLog(`Stored at memory address starting: 0x${addrNum.toString(16).padStart(3, "0")}`);

        const bytes = [binary & 0xff, (binary >> 8) & 0xff, (binary >> 16) & 0xff, (binary >> 24) & 0xff];
        bytes.forEach((b, i) => {
          const a = addrNum + i;
          if (a >= 0 && a < newMemory.length) newMemory[a] = b.toString(16).padStart(2, "0").toUpperCase();
        });
        addLog(`Memory bytes stored (little-endian): ${bytes.map((b) => b.toString(16).padStart(2, "0").toUpperCase()).join(" ")}`);

        currentPC += 4n;
      }

      // -------- S-TYPE --------
      else if (STypeInstructions.includes(instr)) {
        if (tokens.length < 3) {
          addLog(`❌ Assembly error line ${index + 1}: Expected 2 operands for ${instr}`);
          addLog("Assembly aborted.");
          return;
        }

        const rs2 = parseRegister(tokens[1]);
        const match = tokens[2].match(/^(-?\d+)\((x\d+)\)$/);
        if (!match) {
          addLog(`❌ Invalid memory operand '${tokens[2]}' on line ${index + 1}`);
          addLog("Assembly aborted.");
          return;
        }

        const immParsed = parseSigned12(match[1]);
        const rs1 = parseRegister(match[2]);
        if ([rs1, rs2].some((x) => Number.isNaN(x)) || immParsed === null) {
          addLog(`❌ Invalid register or immediate in '${lines[index]}'`);
          addLog("Assembly aborted.");
          return;
        }

        const binary = encodeSType(instr, rs1, rs2, immParsed);
        newMap[index] = addrNum;
        newAddrToLine[addrNum] = index;

        addLog(`Line ${index + 1}: ${line}`);
        addLog(`Binary: ${formatBinary32(binary)}`);
        addLog(`Hex: ${toHex32(binary)}`);
        addLog(`Stored at memory address starting: 0x${addrNum.toString(16).padStart(3, "0")}`);

        const bytes = [binary & 0xff, (binary >> 8) & 0xff, (binary >> 16) & 0xff, (binary >> 24) & 0xff];
        bytes.forEach((b, i) => {
          const a = addrNum + i;
          if (a >= 0 && a < newMemory.length) newMemory[a] = b.toString(16).padStart(2, "0").toUpperCase();
        });
        addLog(`Memory bytes stored (little-endian): ${bytes.map((b) => b.toString(16).padStart(2, "0").toUpperCase()).join(" ")}`);

        currentPC += 4n;
      }

      // -------- U-TYPE --------
      else if (UTypeInstructions.includes(instr)) {
        if (tokens.length < 3) {
          addLog(`❌ Assembly error line ${index + 1}: Expected 2 operands for ${instr}`);
          addLog("Assembly aborted.");
          return;
        }
        const rd = parseRegister(tokens[1]);
        const immParsed = parseDecimal(tokens[2]);
        if (Number.isNaN(rd) || immParsed === null) {
          addLog(`❌ Invalid register or immediate in '${lines[index]}'`);
          addLog("Assembly aborted.");
          return;
        }

        const binary = encodeUType(instr, rd, immParsed);
        newMap[index] = addrNum;
        newAddrToLine[addrNum] = index;

        addLog(`Line ${index + 1}: ${lines[index]}`);
        addLog(`Binary: ${formatBinary32(binary)}`);
        addLog(`Hex: ${toHex32(binary)}`);
        addLog(`Stored at memory address starting: 0x${addrNum.toString(16).padStart(3, "0")}`);

        const bytes = [binary & 0xff, (binary >> 8) & 0xff, (binary >> 16) & 0xff, (binary >> 24) & 0xff];
        bytes.forEach((b, i) => {
          const a = addrNum + i;
          if (a >= 0 && a < newMemory.length) newMemory[a] = b.toString(16).padStart(2, "0").toUpperCase();
        });

        currentPC += 4n;
      }

      // -------- SB-TYPE (branches) --------
      else if (SBTypeInstructions.includes(instr)) {
        // beq/bne/blt/bge xRS1, xRS2, label
        if (tokens.length < 4) {
          addLog(`❌ Assembly error line ${index + 1}: Expected 3 operands for ${instr}`);
          addLog("Assembly aborted.");
          return;
        }

        const rs1 = parseRegister(tokens[1]);
        const rs2 = parseRegister(tokens[2]);
        const label = tokens[3];

        if ([rs1, rs2].some((x) => Number.isNaN(x))) {
          addLog(`❌ Invalid register in '${line}'`);
          addLog("Assembly aborted.");
          return;
        }
        if (labelMap[label] === undefined) {
          addLog(`❌ Undefined label '${label}' on line ${index + 1}`);
          addLog("Assembly aborted.");
          return;
        }

        const targetPC = labelMap[label];
        const offset = targetPC - currentPC;
        const offNum = Number(offset);

        if (offNum % 2 !== 0) {
          addLog(`❌ Branch offset not 2-byte aligned on line ${index + 1} (offset=${offNum})`);
          addLog("Assembly aborted.");
          return;
        }
        if (offNum < -4096 || offNum > 4094) {
          addLog(`❌ Branch offset out of range on line ${index + 1} (offset=${offNum})`);
          addLog("Assembly aborted.");
          return;
        }

        const binary = encodeSBType(instr, rs1, rs2, offNum);
        newMap[index] = addrNum;
        newAddrToLine[addrNum] = index;
        newBranchOffsets[index] = offset;

        addLog(`Line ${index + 1}: ${line}`);
        addLog(`Binary: ${formatBinary32(binary)}`);
        addLog(`Hex: ${toHex32(binary)}`);
        addLog(`Stored at memory address starting: 0x${addrNum.toString(16).padStart(3, "0")}`);
        addLog(`Resolved label '${label}' @ 0x${Number(targetPC).toString(16).padStart(3, "0")} ; offset = ${offNum}`);

        const bytes = [binary & 0xff, (binary >> 8) & 0xff, (binary >> 16) & 0xff, (binary >> 24) & 0xff];
        bytes.forEach((b, i) => {
          const a = addrNum + i;
          if (a >= 0 && a < newMemory.length) newMemory[a] = b.toString(16).padStart(2, "0").toUpperCase();
        });
        addLog(`Memory bytes stored (little-endian): ${bytes.map((b) => b.toString(16).padStart(2, "0").toUpperCase()).join(" ")}`);

        currentPC += 4n;
      }

      // -------- UJ-TYPE (jal) --------
      else if (UJTypeInstructions.includes(instr)) {
        // jal rd, label   (also supports jal x1, -8 as numeric offset if you want)
        if (tokens.length < 3) {
          addLog(`❌ Assembly error line ${index + 1}: Expected 2 operands for jal`);
          addLog("Assembly aborted.");
          return;
        }

        const rd = parseRegister(tokens[1]);
        const targetToken = tokens[2];

        if (Number.isNaN(rd)) {
          addLog(`❌ Invalid register in '${line}'`);
          addLog("Assembly aborted.");
          return;
        }

        let offset;
        let targetPC;

        // label target
        if (labelMap[targetToken] !== undefined) {
          targetPC = labelMap[targetToken];
          offset = targetPC - currentPC;
        } else {
          // allow numeric byte offset (DEC) as fallback
          const n = parseDecimal(targetToken);
          if (n === null) {
            addLog(`❌ Undefined label '${targetToken}' on line ${index + 1}`);
            addLog("Assembly aborted.");
            return;
          }
          offset = BigInt(n);
          targetPC = currentPC + offset;
        }

        const offNum = Number(offset);

        // jal immediate: multiple of 2, range ±1MiB
        if (offNum % 2 !== 0) {
          addLog(`❌ jal offset not 2-byte aligned on line ${index + 1} (offset=${offNum})`);
          addLog("Assembly aborted.");
          return;
        }
        if (offNum < -1048576 || offNum > 1048574) {
          addLog(`❌ jal offset out of range on line ${index + 1} (offset=${offNum})`);
          addLog("Assembly aborted.");
          return;
        }

        const binary = encodeUJType(rd, offNum);
        newMap[index] = addrNum;
        newAddrToLine[addrNum] = index;

        const targetAddrNum = Number(targetPC);
        newJalTargets[index] = targetAddrNum;

        addLog(`Line ${index + 1}: ${line}`);
        addLog(`Binary: ${formatBinary32(binary)}`);
        addLog(`Hex: ${toHex32(binary)}`);
        addLog(`Stored at memory address starting: 0x${addrNum.toString(16).padStart(3, "0")}`);
        addLog(`jal target = 0x${targetAddrNum.toString(16).padStart(3, "0")} ; offset = ${offNum}`);

        const bytes = [binary & 0xff, (binary >> 8) & 0xff, (binary >> 16) & 0xff, (binary >> 24) & 0xff];
        bytes.forEach((b, i) => {
          const a = addrNum + i;
          if (a >= 0 && a < newMemory.length) newMemory[a] = b.toString(16).padStart(2, "0").toUpperCase();
        });
        addLog(`Memory bytes stored (little-endian): ${bytes.map((b) => b.toString(16).padStart(2, "0").toUpperCase()).join(" ")}`);

        currentPC += 4n;
      }

      else {
        addLog(`❌ Unknown instruction on line ${index + 1}: ${instr}`);
        addLog("Assembly aborted.");
        return;
      }

      addLog("");
    }

    setMemory(newMemory);
    setAssembledMap(newMap);
    setBranchOffsetMap(newBranchOffsets);
    setAddrToLineMap(newAddrToLine);
    setJalTargetAddrMap(newJalTargets);
    setAssemblyOk(true);
    setExecutionHalted(false);
    addLog("----- Assembly complete -----\n");
  };


  // ---------------- EXECUTE SINGLE INSTRUCTION (returns nextPC/nextLine) ----------------
  // ---------------- EXECUTE SINGLE INSTRUCTION (PC-driven) ----------------
const executeInstructionFromSource = (srcLine, srcLineIndex, currentPC) => {
  const { remainder } = extractLabelsAndRemainder(srcLine);
  const clean = remainder;

  const tokens = clean.split(/[\s,]+/).filter(Boolean);
  if (tokens.length === 0) {
    return { ok: true, nextPC: currentPC + 4n };
  }

  const instr = tokens[0].toLowerCase();

  try {
    // ======================================================
    // R-TYPE
    // ======================================================
    if (RTypeInstructions.includes(instr)) {
      const rd = parseRegister(tokens[1]);
      const rs1 = parseRegister(tokens[2]);
      const rs2 = parseRegister(tokens[3]);

      const a = BigInt.asIntN(64, BigInt("0x" + registersRef.current[rs1]));
      const b = BigInt.asIntN(64, BigInt("0x" + registersRef.current[rs2]));

      let res = 0n;

      switch (instr) {
        case "add": res = a + b; break;
        case "sub": res = a - b; break;
        case "xor": res = BigInt.asUintN(64, a) ^ BigInt.asUintN(64, b); break;
        case "or":  res = BigInt.asUintN(64, a) | BigInt.asUintN(64, b); break;
        case "and": res = BigInt.asUintN(64, a) & BigInt.asUintN(64, b); break;
        case "sll": res = BigInt.asUintN(64, a << (b & 0x3fn)); break;
        case "srl": res = BigInt.asUintN(64, a >> (b & 0x3fn)); break;
        case "mul": res = a * b; break;
        case "div": res = b === 0n ? 0n : a / b; break;
        default: return { ok: false, nextPC: currentPC };
      }

      writeReg(rd, BigInt.asUintN(64, res).toString(16).padStart(16,"0").toUpperCase());
      if (rd !== 0) addLog(`x${rd} = ${BigInt.asIntN(64, res)} (DEC)`);

      return { ok: true, nextPC: currentPC + 4n };
    }

    // ======================================================
    // I-TYPE ARITH + SHIFTS
    // ======================================================
    if (["addi","xori","ori","andi"].includes(instr)) {
      const rd = parseRegister(tokens[1]);
      const rs1 = parseRegister(tokens[2]);
      const imm = parseSigned12(tokens[3]);
      if (imm === null) {
        addLog("❌ Invalid immediate");
        return { ok:false, nextPC: currentPC };
      }

      const a = BigInt.asIntN(64, BigInt("0x" + registersRef.current[rs1]));
      let res = 0n;

      if (instr==="addi") res = a + BigInt(imm);
      if (instr==="xori") res = BigInt.asUintN(64,a) ^ BigInt(imm);
      if (instr==="ori")  res = BigInt.asUintN(64,a) | BigInt(imm);
      if (instr==="andi") res = BigInt.asUintN(64,a) & BigInt(imm);

      writeReg(rd, BigInt.asUintN(64,res).toString(16).padStart(16,"0").toUpperCase());
      return { ok:true, nextPC: currentPC + 4n };
    }



    if (["slli","srli"].includes(instr)) {
      const rd = parseRegister(tokens[1]);
      const rs1 = parseRegister(tokens[2]);
      const shamt = parseDecimal(tokens[3]);
      if (shamt === null || shamt < 0 || shamt > 63) {
        addLog("❌ Invalid shift amount");
        return { ok:false, nextPC: currentPC };
      }

      const a = BigInt.asUintN(64, BigInt("0x" + registersRef.current[rs1]));
      const res =
        instr==="slli" ? (a << BigInt(shamt)) : (a >> BigInt(shamt));

      writeReg(rd, BigInt.asUintN(64,res).toString(16).padStart(16,"0").toUpperCase());
      return { ok:true, nextPC: currentPC + 4n };
    }



    // ======================================================
    // U-TYPE (LUI)
    // ======================================================
    if (instr === "lui") {
      const rd = parseRegister(tokens[1]);
      const imm = parseDecimal(tokens[2]);
      if (imm === null || imm < -(1<<19) || imm >= (1<<19)) {
        addLog("❌ LUI immediate out of range");
        return { ok:false, nextPC: currentPC };
      }
      const value = BigInt.asIntN(32, BigInt(imm) << 12n);

      writeReg(rd, BigInt.asUintN(64, value).toString(16).padStart(16,"0").toUpperCase());
      if (rd !== 0) addLog(`x${rd} = ${value} (DEC)`);

      return { ok: true, nextPC: currentPC + 4n };
    }

    // ======================================================
    // LOADS
    // ======================================================
    // ======================================================
    // LOADS (lb, lbu, lh, lhu, lw, lwu, ld)
    // ======================================================
    if (["lb","lbu","lh","lhu","lw","lwu","ld"].includes(instr)) {
      const rd = parseRegister(tokens[1]);

      const m = tokens[2].match(/^(-?\d+)\((x\d+)\)$/);
      if (!m) {
        addLog(`❌ Invalid load operand '${tokens[2]}'`);
        return { ok: false, nextPC: currentPC };
      }

      const [, immStr, rs1Str] = m;
      const imm = parseSigned12(immStr);
      const rs1 = parseRegister(rs1Str);

      if (imm === null || Number.isNaN(rd) || Number.isNaN(rs1)) {
        addLog(`❌ Invalid register or immediate in '${instr}'`);
        return { ok: false, nextPC: currentPC };
      }

      const addr = Number(BigInt("0x" + registersRef.current[rs1]) + BigInt(imm));

      // ---- determine access size ----
      let size = 1;
      if (instr === "lh" || instr === "lhu") size = 2;
      if (instr === "lw" || instr === "lwu") size = 4;
      if (instr === "ld") size = 8;

      // ---- bounds check ----
      if (addr < 0 || addr + size > memory.length) {
        addLog(`❌ Memory load out of bounds @ 0x${addr.toString(16)}`);
        return { ok: false, nextPC: currentPC };
      }

      const read = (a) => BigInt(parseInt(memory[a], 16));
      let v = 0n;

      // ---- perform load ----
      if (instr === "lb" || instr === "lbu") {
        v = read(addr);
        if (instr === "lb") v = BigInt.asIntN(8, v);
      }

      if (instr === "lh" || instr === "lhu") {
        v = read(addr) | (read(addr + 1) << 8n);
        if (instr === "lh") v = BigInt.asIntN(16, v);
      }

      if (instr === "lw" || instr === "lwu") {
        v =
          read(addr) |
          (read(addr + 1) << 8n) |
          (read(addr + 2) << 16n) |
          (read(addr + 3) << 24n);
        if (instr === "lw") v = BigInt.asIntN(32, v);
      }

      if (instr === "ld") {
        for (let i = 0; i < 8; i++) {
          v |= read(addr + i) << BigInt(i * 8);
        }
        v = BigInt.asIntN(64, v);
      }

      writeReg(
        rd,
        BigInt.asUintN(64, v)
          .toString(16)
          .padStart(16, "0")
          .toUpperCase()
      );

      if (rd !== 0) addLog(`x${rd} ← ${v} (DEC)`);

      return { ok: true, nextPC: currentPC + 4n };
    }


    // ======================================================
    // STORES
    // ======================================================
   // ======================================================
    // STORES (sb, sh, sw, sd)
    // ======================================================
    if (["sb","sh","sw","sd"].includes(instr)) {
      const rs2 = parseRegister(tokens[1]);

      const m = tokens[2].match(/^(-?\d+)\((x\d+)\)$/);
      if (!m) {
        addLog(`❌ Invalid store operand '${tokens[2]}'`);
        return { ok: false, nextPC: currentPC };
      }

      const [, immStr, rs1Str] = m;
      const imm = parseSigned12(immStr);
      const rs1 = parseRegister(rs1Str);

      if (imm === null || Number.isNaN(rs1) || Number.isNaN(rs2)) {
        addLog(`❌ Invalid register or immediate in '${instr}'`);
        return { ok: false, nextPC: currentPC };
      }

      const addr = Number(BigInt("0x" + registersRef.current[rs1]) + BigInt(imm));
      const val = BigInt("0x" + registersRef.current[rs2]);

      // ---- determine access size ----
      let size = 1;
      if (instr === "sh") size = 2;
      if (instr === "sw") size = 4;
      if (instr === "sd") size = 8;

      // ---- bounds check ----
      if (addr < 0 || addr + size > memory.length) {
        addLog(`❌ Memory store out of bounds @ 0x${addr.toString(16)}`);
        return { ok: false, nextPC: currentPC };
      }

      // ---- perform store (little-endian) ----
      const newMem = [...memory];

      const writeByte = (a, v) => {
        newMem[a] = Number(v & 0xffn)
          .toString(16)
          .padStart(2, "0")
          .toUpperCase();
      };

      if (instr === "sb") {
        writeByte(addr, val);
      }

      if (instr === "sh") {
        writeByte(addr, val);
        writeByte(addr + 1, val >> 8n);
      }

      if (instr === "sw") {
        for (let i = 0; i < 4; i++) {
          writeByte(addr + i, val >> BigInt(i * 8));
        }
      }

      if (instr === "sd") {
        for (let i = 0; i < 8; i++) {
          writeByte(addr + i, val >> BigInt(i * 8));
        }
      }

      setMemory(newMem);

      addLog(`${instr} → mem[0x${addr.toString(16)}]`);
      return { ok: true, nextPC: currentPC + 4n };
    }


    // ======================================================
    // BRANCHES
    // ======================================================
    if (SBTypeInstructions.includes(instr)) {
      const rs1 = parseRegister(tokens[1]);
      const rs2 = parseRegister(tokens[2]);
      const off = branchOffsetMap[srcLineIndex];

      const a = BigInt.asIntN(64, BigInt("0x" + registersRef.current[rs1]));
      const b = BigInt.asIntN(64, BigInt("0x" + registersRef.current[rs2]));

      let take = false;
      if (instr==="beq") take = a===b;
      if (instr==="bne") take = a!==b;
      if (instr==="blt") take = a<b;
      if (instr==="bge") take = a>=b;

      return { ok:true, nextPC: take ? currentPC+off : currentPC+4n };
    }

    // ======================================================
    // JAL
    // ======================================================
    if (instr === "jal") {
      const rd = parseRegister(tokens[1]);
      const ret = currentPC + 4n;

      writeReg(rd, ret.toString(16).padStart(16,"0").toUpperCase());
      if (rd !== 0) addLog(`x${rd} = PC+4`);

      return { ok:true, nextPC: BigInt(jalTargetAddrMap[srcLineIndex]) };
    }

    // ======================================================
    // ======================================================
    // JALR
    // ======================================================
    if (instr === "jalr") {
      const rd = parseRegister(tokens[1]);

      const m = tokens[2].match(/^(-?\d+)\((x\d+)\)$/);
      if (!m) {
        addLog(`❌ Invalid operand '${tokens[2]}'`);
        return { ok: false, nextPC: currentPC };
      }

      const [, immStr, rs1Str] = m;
      const imm = parseSigned12(immStr);
      const rs1 = parseRegister(rs1Str);

      // ✅ REQUIRED validation
      if (imm === null || Number.isNaN(rs1) || Number.isNaN(rd)) {
        addLog(`❌ Invalid register or immediate in jalr`);
        return { ok: false, nextPC: currentPC };
      }

      const ret = currentPC + 4n;
      writeReg(rd, ret.toString(16).padStart(16, "0").toUpperCase());

      let target = BigInt("0x" + registersRef.current[rs1]) + BigInt(imm);
      target &= ~1n; // clear LSB per spec

      return { ok: true, nextPC: target };
    }


    addLog(`❌ Unknown instruction '${instr}'`);
    return { ok:false, nextPC: currentPC };

  } catch (e) {
    addLog(`❌ Exception: ${e}`);
    return { ok:false, nextPC: currentPC };
  }
};



  // ---------------- STEP ----------------
  const executeStep = () => {
  if (!assemblyOk) {
    addLog("❌ Cannot execute: assemble first.");
    return;
  }
  if (executionHalted) {
    addLog("❌ Execution halted. Re-assemble.");
    return;
  }

  const lineIdx = addrToLineMap[Number(pc)];
  if (lineIdx === undefined) {
    addLog("✔ Program finished.");
    return;
  }

  const shown = extractLabelsAndRemainder(lines[lineIdx]).remainder;
  addLog(`Line ${lineIdx + 1}: ${shown}`);

  const result = executeInstructionFromSource(lines[lineIdx], lineIdx, pc);
  setPc(result.nextPC);

  if (!result.ok) {
    setExecutionHalted(true);
    addLog("❌ Execution stopped due to error.");
  }
};

const togglePause = () => {
  if (!runRef.current) {
    addLog("ℹ Not running");
    return;
  }

  pauseRef.current = !pauseRef.current;
  addLog(pauseRef.current ? "⏸ Paused" : "▶ Resumed");
};



  // ---------------- RUN PROGRAM ----------------
  
  const runProgram = async () => {
  if (!assemblyOk) {
    addLog("❌ Cannot run: assemble first.");
    return;
  }
  if (executionHalted) {
    addLog("❌ Execution halted. Re-assemble.");
    return;
  }

  // ✅ prevent double run
  if (runRef.current) {
    pauseRef.current = false;
    addLog("▶ Resumed");
    return;
  }

  let pcLocal = pc;

  if (addrToLineMap[Number(pcLocal)] === undefined) {
    addLog("✔ Program finished.");
    return;
  }

  const maxSteps = 20000;
  let steps = 0;

  runRef.current = true;

  while (runRef.current) {
    if (pauseRef.current) {
      await new Promise((res) => setTimeout(res, 100));
      continue;
    }

    if (steps++ > maxSteps) {
      addLog("❌ Safety stop: too many steps (possible infinite loop).");
      setExecutionHalted(true);
      break;
    }

    const lineIdx = addrToLineMap[Number(pcLocal)];
    if (lineIdx === undefined) {
      // addLog("✔ Program finished.");
      break;
    }

    const { remainder } = extractLabelsAndRemainder(lines[lineIdx]);
    if (!remainder) {
      pcLocal += 4n;
      continue;
    }

    addLog(`Line ${lineIdx + 1}: ${remainder}`);

    const result = executeInstructionFromSource(
      lines[lineIdx],
      lineIdx,
      pcLocal
    );

    pcLocal = result.nextPC;
    setPc(pcLocal);

    await new Promise((res) => setTimeout(res, 200));

    if (!result.ok) {
      setExecutionHalted(true);
      addLog("❌ Execution stopped due to error.");
      break;
    }
  }

  // ✅ CRITICAL CLEANUP
  runRef.current = false;
  pauseRef.current = false;

  if (!executionHalted) {
    addLog("✔ Program finished running.");
  }
};



    const pauseProgram = () => {
      pauseRef.current = true;
    };

    const resumeProgram = () => {
      pauseRef.current = false;
    };



  // ---------------- RESET ----------------
  const handleReset = () => {
    runRef.current = false;
    pauseRef.current = false;

    const fresh = createInitialState();
    setRegisters(fresh.registers);
    setPc(fresh.pc);
    setMemory(fresh.memory);
    setLog([]);
    setMemPage(0);
    setCurrentLine(0);
    setAssembledMap({});
    setBranchOffsetMap({});
    setJalTargetAddrMap({});
    setAddrToLineMap({});
    setHighlightedAddrs([]);
    setAssemblyOk(false);
    setExecutionHalted(false);
    addLog("System Reset: All registers, memory and PC restored.");
  };

  // ---------------- UI ----------------
  return (
    <div style={{ height: "100vh", display: "flex", background: "#121212", color: "white", fontFamily: "monospace" }}>
      <div style={{ width: "35%", borderRight: "2px solid #333", padding: "10px", display: "flex", flexDirection: "column" }}>
        <CodeEditor
          code={code}
          setCode={setCode}
          lines={lines}
          currentLine={currentLine}
          setCurrentLine={setCurrentLine}
          activeRunButton={activeRunButton}
          setActiveRunButton={setActiveRunButton}
          assembleCode={assembleCode}
          executeStep={executeStep}
          handleReset={handleReset}
          runProgram={runProgram}
          pauseProgram={pauseProgram}
          resumeProgram={resumeProgram}
          togglePause={togglePause}


        />
      </div>

      <div style={{ width: "65%", display: "flex", flexDirection: "column", minHeight: 0 }}>
        <div style={{ flex: 0.7, display: "flex", minHeight: 0, overflow: "hidden" }}>
          <Registers
            registers={registers}
            setRegisters={setRegisters}
            pc={pc}
            setPc={setPc}
            regView={regView}
            setRegView={setRegView}
            addLog={addLog}
          />
          <Memory
            memory={memory}
            setMemory={setMemory}
            memView={memView}
            setMemView={setMemView}
            memPage={memPage}
            setMemPage={setMemPage}
            highlightedAddrs={highlightedAddrs}
            SEGMENTS={SEGMENTS}
            addLog={addLog}
            registers={registers}
          />
        </div>

        <div style={{ flex: 0.25, minHeight: 0 }}>
          <Log log={log} />
        </div>
      </div>
    </div>
  );
}
